import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetPerformanceTestsQuery, useLazyGetPerformanceTestWidgetDataQuery } from "store/api/performanceTestApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDataToPerformanceTestTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"

import { dummyDevices, dummyMeta } from "../MonitoringPage/dummyMonitoringData"
import { dummyPerfTestWidgetData } from "./dummyPerfTestData"
import PerformanceTestContent from "./PerformanceTestContent"
import performanceTestTableHeaderCells from "./performanceTestTableHeaderCells"

const initialState = {
  scheduled: 0,
  rescheduled: 0,
  passed: 0,
  issue: 0,
  running: 0,
}

const PerformanceTestPage = () => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [triggerGetPerformanceTest, { isFetching, data }] = useLazyGetPerformanceTestsQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const requestParams = { phisical_status: "installed", ...urlQueryParams }
  const [triggerGetPerformanceTestWidgetData, { isFetching: isFetchingWidgetData }] =
    useLazyGetPerformanceTestWidgetDataQuery()
  const [widgetData, setWidgetData] = useState(initialState)
  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const widgetInfo = hasPerformanceTestAccess ? widgetData : dummyPerfTestWidgetData
  const isFetchingData = isFetching && !data
  const meta = hasPerformanceTestAccess ? data?.meta : dummyMeta
  const devices = useMemo(() => {
    if (!hasPerformanceTestAccess) {
      return dummyDevices
    }

    return data?.data || []
  }, [data, hasPerformanceTestAccess])

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_performance_test_list" })
  }, [])

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(performanceTestTableHeaderCells, columnsData?.data?.config)
        : performanceTestTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: requestParams,
    headerCells: tableHeaderCells,
    meta: meta,
    fileName: "performance_tests",
    convertData: convertDataToPerformanceTestTableRows,
    useLazyQuery: useLazyGetPerformanceTestsQuery,
  })

  const sendRequest = async (params: UITypes.Params) => {
    if (!hasPerformanceTestAccess) {
      return
    }

    try {
      const res = await triggerGetPerformanceTest(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchPerformanceTestWidgetData = async () => {
    try {
      const res = await triggerGetPerformanceTestWidgetData()
      if (res?.data) {
        setWidgetData({
          scheduled: res?.data?.scheduled,
          rescheduled: res?.data?.rescheduled,
          passed: res?.data?.passed,
          issue: res?.data?.issue,
          running: res?.data?.running,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    dispatch(setSelectedDevicesForPerformanceTest({ devices: [] }))

    if (hasPerformanceTestAccess) {
      fetchPerformanceTestWidgetData()
    }
  }, [])

  const fetchPerformanceTestData = async () => {
    sendRequest(requestParams)
  }

  useEffect(() => {
    fetchPerformanceTestData()
  }, [urlQueryParams])

  const handleActionSuccess = () => {
    fetchPerformanceTestData()
    fetchPerformanceTestWidgetData()
  }

  return (
    <>
      <PerformanceTestContent
        devices={devices}
        meta={meta}
        isFetching={isFetchingData}
        widgetData={widgetInfo}
        isFetchingWidgetData={isFetchingWidgetData}
        onActionSuccess={handleActionSuccess}
        tableHeaderCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        onExport={handleExport}
      />
      {!hasPerformanceTestAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.PerformanceTest} />}
    </>
  )
}

export default PerformanceTestPage
